import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Hero3D from "../components/Hero3D";
import ProductCard from "../components/ProductCard";
import ProductDetail from "../components/ProductDetail";
import { Reveal, MaskedLines } from "../components/Reveal";
import { useQuote } from "../context/QuoteContext";
import { categories, products, strengths, industries } from "../data/products";
import { clients } from "../data/clients";
import Seo from "../components/Seo";

const ease = [0.16, 1, 0.3, 1];
const featured = products.filter((p) => p.featured);
const trustLogos = clients.filter((c) => c.logo).slice(0, 6);

export default function Home() {
  const { openQuote } = useQuote();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [detail, setDetail] = useState(null);
  const [focus, setFocus] = useState(null);
  const [par, setPar] = useState({ x: 0, y: 0 });

  const onEcoMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setPar({ x: ((e.clientX - r.left) / r.width - 0.5) * 2, y: ((e.clientY - r.top) / r.height - 0.5) * 2 });
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="Clinixport Global | Medical, Healthcare & Hygiene Supply"
        description="Clinixport Global supplies medical equipment, PPE and protective hygiene products — nitrile & latex gloves, face masks, head covers, shoe covers and aprons — for healthcare, food and industrial environments. Request a quote."
        path="/"
        keywords="medical equipment supplier, medical supplies, healthcare supplies, PPE, medical gloves, nitrile gloves, latex gloves, face masks, hospital supplies, protective hygiene products"
      />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center relative z-10">
          <div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.6 }}
              className="text-xs font-bold uppercase tracking-[0.3em] text-[#1F6FEB] mb-6"
            >
              Medical · Healthcare · Hygiene Supply
            </motion.p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-[#0A1128] leading-[1.02]" data-testid="hero-heading">
              <MaskedLines start={0.15} lines={["Protection.", "Precision."]} />
              <span className="line-mask block">
                <motion.span
                  className="block text-[#1F6FEB]"
                  initial={{ y: "110%" }} animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease, delay: 0.15 + 2 * 0.11 }}
                >
                  Healthcare Supply.
                </motion.span>
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-7 text-lg text-[#475569] leading-relaxed max-w-lg"
            >
              Clinixport Global supplies medical equipment, protective products and hygiene
              essentials for healthcare environments where quality and reliability matter — from
              a portfolio built to scale with your operation.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <button onClick={() => openQuote()} data-testid="hero-request-quote"
                className="inline-flex items-center gap-2 bg-[#1F6FEB] text-white hover:bg-[#1556B8] transition-colors px-8 py-4 font-bold text-sm tracking-widest uppercase">
                Request a Quote <ArrowRight size={18} />
              </button>
              <Link to="/products" data-testid="hero-explore-products"
                className="inline-flex items-center gap-2 border border-[#0A1128] text-[#0A1128] hover:bg-[#0A1128] hover:text-white transition-all px-8 py-4 font-bold text-sm tracking-widest uppercase">
                Explore Products
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }}
            className="relative h-[360px] sm:h-[460px] lg:h-[560px]"
          >
            <Hero3D />
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="border-y border-[#0A1128]/10 bg-white py-6">
        <Marquee speed={40} gradient={false} autoFill>
          {["Quality Assured", "Global Reach", "Hygiene Standards", "Reliable Supply", "Trusted Partnership", "Cleanliness First"].map((t, i) => (
            <span key={i} className="mx-16 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#0A1128]/25 uppercase flex items-center gap-16">
              {t} <span className="text-[#1F6FEB]">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* BRAND INTRO */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-3">
            <Reveal><p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB]">Who We Are</p></Reveal>
          </div>
          <div className="lg:col-span-9">
            <Reveal>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1128] leading-[1.15]">
                A healthcare supply partner built around one standard: <span className="text-[#1F6FEB]">cleanliness cannot be compromised.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg text-[#475569] leading-relaxed max-w-3xl">
                We supply protective and hygiene essentials today — masks, gloves, head covers,
                shoe covers and protective wear — as the first featured range from a much wider
                medical and hospital equipment portfolio. Every product is selected and supplied
                to protect people, standards and reputations.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-[#0A1128] hover:text-[#1F6FEB] transition-colors group">
                Discover Clinixport <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section className="bg-white border-y border-[#0A1128]/10 py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-4">Product Groups</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1128]">Explore the range</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/products" className="inline-flex items-center gap-2 text-sm font-bold text-[#0A1128] hover:text-[#1F6FEB] transition-colors">
                All products <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0A1128]/10 border border-[#0A1128]/10">
            {categories.map((c, i) => (
              <CategoryCard key={c.id} c={c} i={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-4">Featured · Phase 1</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1128] max-w-2xl">Selected products, ready to supply</h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.slice(0, 6).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onView={setDetail} />
          ))}
        </div>
      </section>

      {/* INTERACTIVE FLOATING ECOSYSTEM */}
      <section className="bg-[#0A1128] text-white py-24 sm:py-32 overflow-hidden relative">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#38bdf8] mb-4">The Clinixport Ecosystem</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              {focus ? focus.categoryName : "One supply partner, every layer of protection"}
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed min-h-[48px]">
              {focus ? focus.spec : "Hover a product to bring it into focus, then select it to explore its category. From head to toe, our protective range works together to keep environments clean and compliant."}
            </p>
          </div>
          <div
            className="mt-16 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4"
            onMouseMove={onEcoMove}
            onMouseLeave={() => { setPar({ x: 0, y: 0 }); setFocus(null); }}
          >
            {products.filter((p) => p.featured).concat(products.find(p=>p.id==='n95-respirator')).slice(0, 6).map((p, i) => {
              const isFocus = focus?.id === p.id;
              const depth = (i % 3) + 1;
              return (
                <motion.button
                  key={p.id}
                  onMouseEnter={() => setFocus(p)}
                  onClick={() => navigate(`/products?category=${p.category}`)}
                  animate={{
                    y: (reduce || isFocus) ? 0 : [0, i % 2 ? -10 : 10, 0],
                    x: par.x * depth * 3,
                    scale: isFocus ? 1.05 : focus ? 0.97 : 1,
                    opacity: focus && !isFocus ? 0.5 : 1,
                  }}
                  transition={{
                    y: { duration: 4 + i, repeat: reduce ? 0 : Infinity, ease: "easeInOut" },
                    x: { type: "spring", stiffness: 60, damping: 18 },
                    scale: { duration: 0.4, ease }, opacity: { duration: 0.4, ease },
                  }}
                  className={`group relative border p-5 flex flex-col items-center transition-colors duration-300 ${isFocus ? "border-[#38bdf8] bg-white/[0.08]" : "border-white/10 bg-white/[0.04] hover:border-[#38bdf8]/60"}`}
                  data-testid={`ecosystem-${p.id}`}
                >
                  <img src={p.img} alt={p.name} loading="lazy" className="h-20 w-auto object-contain mix-blend-screen transition-transform duration-500 group-hover:scale-110" />
                  <span className="mt-4 text-xs font-bold tracking-wide text-white/70 group-hover:text-white text-center">{p.categoryName}</span>
                  <span className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-[#38bdf8] opacity-0 group-hover:opacity-100 transition-opacity">View category →</span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CLINIXPORT — numbered manifesto */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-4">Why Clinixport</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1128] max-w-2xl">Four commitments behind every order</h2>
        </Reveal>
        <div className="mt-16 divide-y divide-[#0A1128]/10 border-t border-[#0A1128]/10">
          {strengths.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.05}>
              <div className="group grid grid-cols-1 md:grid-cols-12 gap-4 py-9 items-start hover:bg-white transition-colors -mx-5 sm:-mx-8 px-5 sm:px-8">
                <span className="md:col-span-2 text-5xl font-extrabold tracking-tighter text-[#0A1128]/15 group-hover:text-[#1F6FEB] transition-colors">{s.num}</span>
                <h3 className="md:col-span-4 text-2xl font-bold tracking-tight text-[#0A1128]">{s.title}</h3>
                <p className="md:col-span-6 text-[#475569] leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="bg-white border-y border-[#0A1128]/10 py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB] mb-4">Industries We Serve</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#0A1128]">Where cleanliness is critical</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/industries" className="inline-flex items-center gap-2 text-sm font-bold text-[#0A1128] hover:text-[#1F6FEB] transition-colors">
                Explore industries <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </div>
          <div className="flex flex-wrap gap-3">
            {industries.map((ind, i) => (
              <Reveal key={ind.id} delay={i * 0.04}>
                <div className="border border-[#0A1128]/15 px-6 py-4 text-[#0A1128] font-bold hover:border-[#1F6FEB] hover:text-[#1F6FEB] transition-colors">
                  {ind.name}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-t border-[#0A1128]/10 py-16 sm:py-20" data-testid="home-trust-strip">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1F6FEB]">
                Trusted by organisations we proudly supply
              </p>
              <Link to="/clients" data-testid="trust-strip-viewall" className="inline-flex items-center gap-2 text-sm font-bold text-[#0A1128] hover:text-[#1F6FEB] transition-colors">
                View all clients <ArrowUpRight size={18} />
              </Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-8 gap-y-10 items-center">
            {trustLogos.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, ease, delay: (i % 6) * 0.05 }}
              >
                <Link
                  to="/clients"
                  aria-label={c.name}
                  data-testid={`trust-logo-${c.id}`}
                  className="flex items-center justify-center h-14 group"
                >
                  <img
                    src={c.logo}
                    alt={`${c.name} logo`}
                    loading="lazy"
                    className="max-h-12 max-w-[78%] w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#0A1128] text-white py-28 sm:py-36 relative overflow-hidden">
        <div className="absolute inset-0 grain" />
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative z-10">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] max-w-3xl">
              Looking for a reliable healthcare supply partner?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => openQuote()} data-testid="final-cta-quote"
                className="inline-flex items-center gap-2 bg-[#1F6FEB] text-white hover:bg-[#1556B8] transition-colors px-9 py-4 font-bold text-sm tracking-widest uppercase">
                Request a Quote <ArrowRight size={18} />
              </button>
              <Link to="/contact"
                className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white hover:text-[#0A1128] transition-all px-9 py-4 font-bold text-sm tracking-widest uppercase">
                Contact Us
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <ProductDetail product={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

const CategoryCard = ({ c, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.6, ease, delay: (i % 3) * 0.06 }}
    className={`group relative bg-white p-8 min-h-[220px] flex flex-col justify-between overflow-hidden ${c.comingSoon ? "" : ""}`}
    data-testid={`category-${c.id}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#64748B]">{c.tag}</p>
        <h3 className="mt-2 text-2xl font-bold tracking-tight text-[#0A1128]">{c.name}</h3>
      </div>
      {c.img && (
        <img src={c.img} alt={c.name} loading="lazy" className="h-16 w-16 object-contain mix-blend-multiply opacity-80 transition-transform duration-500 group-hover:scale-110" />
      )}
    </div>
    <div className="flex items-center justify-between">
      <span className={`text-sm font-semibold ${c.comingSoon ? "text-[#0D9488]" : "text-[#475569]"}`}>{c.count}</span>
      {!c.comingSoon && (
        <Link to="/products" className="text-[#1F6FEB] opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight size={20} />
        </Link>
      )}
    </div>
    {c.comingSoon && <span className="absolute top-0 right-0 bg-[#0D9488] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5">Expanding</span>}
  </motion.div>
);
